import { useState, useEffect, useCallback } from "react";
import {
  Zap, Building2, ToggleLeft, ToggleRight, Loader2, Search,
  Shield, ChevronRight, Info, RefreshCw
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "../../components/ui/tooltip";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const FeatureFlagsPage = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState({});
  const [tenantOverrides, setTenantOverrides] = useState({});
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("__global__");
  const [tenantFlags, setTenantFlags] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [toggling, setToggling] = useState(null);

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      const [flagsRes, tenantsRes] = await Promise.all([
        api.get("/admin/feature-flags"),
        api.get("/tenants"),
      ]);
      setFeatures(flagsRes.data.features || {});
      setTenantOverrides(flagsRes.data.tenant_overrides || {});
      const tenantData = tenantsRes.data;
      setTenants(Array.isArray(tenantData) ? tenantData : tenantData.tenants || []);
    } catch (e) {
      toast.error("Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  useEffect(() => {
    if (selectedTenant === "__global__") {
      setTenantFlags({});
      return;
    }
    const fetchTenantFlags = async () => {
      try {
        const res = await api.get(`/admin/feature-flags/${selectedTenant}`);
        setTenantFlags(res.data.flags || {});
      } catch (e) {
        console.error(e);
      }
    };
    fetchTenantFlags();
  }, [selectedTenant]);

  const handleToggle = async (featureKey) => {
    if (selectedTenant === "__global__") {
      toast.info("Select a tenant to toggle feature flags");
      return;
    }
    setToggling(featureKey);
    try {
      const currentlyEnabled = tenantFlags[featureKey]?.enabled ?? features[featureKey]?.default ?? true;
      await api.put(`/admin/feature-flags/${selectedTenant}/${featureKey}`, {
        enabled: !currentlyEnabled,
      });
      toast.success(`${features[featureKey]?.label || featureKey} ${!currentlyEnabled ? "enabled" : "disabled"}`);
      // Refresh tenant flags
      const res = await api.get(`/admin/feature-flags/${selectedTenant}`);
      setTenantFlags(res.data.flags || {});
      // Refresh global overrides
      const globalRes = await api.get("/admin/feature-flags");
      setTenantOverrides(globalRes.data.tenant_overrides || {});
    } catch (e) {
      toast.error("Failed to toggle flag");
    } finally {
      setToggling(null);
    }
  };

  const featureEntries = Object.entries(features).filter(([key, meta]) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return key.toLowerCase().includes(q) || meta.label?.toLowerCase().includes(q) || meta.description?.toLowerCase().includes(q);
  });

  const getEffectiveState = (key) => {
    if (selectedTenant !== "__global__" && tenantFlags[key]) {
      return tenantFlags[key].enabled;
    }
    return features[key]?.default ?? false;
  };

  const getOverrideCount = (key) => {
    let count = 0;
    for (const tid in tenantOverrides) {
      if (tenantOverrides[tid][key] !== undefined) count++;
    }
    return count;
  };

  const selectedTenantName = tenants.find(t => t.id === selectedTenant)?.name || "All Tenants";

  if (loading) {
    return (
      <AdminLayout title="Feature Flags">
        <div className="flex items-center justify-center h-64" data-testid="feature-flags-loading">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Feature Flags">
      <TooltipProvider>
        <div className="space-y-6" data-testid="feature-flags-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="feature-flags-title">Feature Flags</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Control platform features globally or per tenant. {Object.keys(features).length} flags configured.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchFlags} data-testid="refresh-flags-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search flags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="feature-flags-search"
              />
            </div>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger className="w-full sm:w-[260px]" data-testid="tenant-selector">
                <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__global__">All Tenants (Global View)</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Info Banner */}
          {selectedTenant === "__global__" && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" data-testid="global-info-banner">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Global View:</strong> Showing default states for all feature flags. Select a specific tenant from the dropdown to toggle flags for that organization.
              </div>
            </div>
          )}

          {/* Feature Flags Grid */}
          <div className="grid gap-3">
            {featureEntries.map(([key, meta]) => {
              const isEnabled = getEffectiveState(key);
              const overrideCount = getOverrideCount(key);
              const isToggling = toggling === key;
              const isTenantSelected = selectedTenant !== "__global__";
              const hasOverride = isTenantSelected && tenantFlags[key] && tenantFlags[key].enabled !== meta.default;

              return (
                <Card
                  key={key}
                  className={cn(
                    "transition-all duration-200",
                    isEnabled ? "border-emerald-200 dark:border-emerald-800/50" : "border-border opacity-80"
                  )}
                  data-testid={`feature-flag-${key}`}
                >
                  <CardContent className="flex items-center justify-between py-4 px-5">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                        isEnabled ? "bg-emerald-100 dark:bg-emerald-900/40" : "bg-muted"
                      )}>
                        <Zap className={cn("w-5 h-5", isEnabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{meta.label}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 font-mono">{key}</Badge>
                          {hasOverride && (
                            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-[10px]">
                              Override
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{meta.description}</p>
                        {overrideCount > 0 && selectedTenant === "__global__" && (
                          <p className="text-[11px] text-blue-600 dark:text-blue-400 mt-1">
                            {overrideCount} tenant override{overrideCount > 1 ? "s" : ""}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <Badge
                        variant={isEnabled ? "default" : "secondary"}
                        className={cn(
                          "text-xs",
                          isEnabled ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : ""
                        )}
                        data-testid={`flag-status-${key}`}
                      >
                        {isEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleToggle(key)}
                            disabled={!isTenantSelected || isToggling}
                            className={cn(
                              "transition-colors",
                              !isTenantSelected && "opacity-40 cursor-not-allowed"
                            )}
                            data-testid={`toggle-${key}`}
                            aria-label={`Toggle ${meta.label}`}
                          >
                            {isToggling ? (
                              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                            ) : isEnabled ? (
                              <ToggleRight className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <ToggleLeft className="w-8 h-8 text-muted-foreground" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {isTenantSelected
                            ? `Click to ${isEnabled ? "disable" : "enable"} for ${selectedTenantName}`
                            : "Select a tenant first"
                          }
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {featureEntries.length === 0 && (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-flags-found">
                <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No feature flags match your search.</p>
              </div>
            )}
          </div>

          {/* Summary Card */}
          <Card className="bg-muted/30">
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 shrink-0" />
                <span>
                  <strong>{Object.values(features).filter((_, i) => getEffectiveState(Object.keys(features)[i])).length}</strong> of{" "}
                  <strong>{Object.keys(features).length}</strong> flags enabled
                  {selectedTenant !== "__global__" && ` for ${selectedTenantName}`}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>
    </AdminLayout>
  );
};

export default FeatureFlagsPage;
